import { motion } from "framer-motion";
import { useEffect, useRef, lazy, Suspense } from "react";

import styled, { ThemeProvider } from "styled-components";

import { YinYang } from "./AllSvgs";
import { Work } from "./WorkData";
import { DarkTheme, mediaQueries } from "./Themes";

import Card from "../subComponents/Card";
import Loading from "../subComponents/Loading";
import Seo from "./Seo";
import PageHeading from "./PageHeading";
import { pages } from "../data/site";
import {
  breadcrumbSchema,
  buildGraph,
  webpageSchema,
} from "../data/schema";

const SocialIcons = lazy(() => import("../subComponents/SocialIcons"));
const PowerButton = lazy(() => import("../subComponents/PowerButton"));
const LogoComponent = lazy(() => import("../subComponents/LogoComponent"));
const BigTitle = lazy(() => import("../subComponents/BigTitle"));

const Box = styled(motion.div)`
  background-color: ${(props) => props.theme.body};
  position: relative;
  display: flex;
  height: 400vh;
`;

const Main = styled(motion.ul)`
  position: fixed;
  top: 6rem;
  left: 0;
  width: 100%;
  height: 40vh;
  display: flex;
  list-style: none;
  margin: 0;
`;

const Rotate = styled.span`
  display: block;
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  width: 80px;
  height: 80px;

  z-index: 1;
  ${mediaQueries(40)`
     width:60px;
         height:60px;   
       svg{
         width:60px;
         height:60px;
       }

  `};
  ${mediaQueries(25)`
        width:50px;
         height:50px;
        svg{
         width:50px;
         height:50px;
       }

  `};
`;
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,

    transition: {
      staggerChildren: 0.5,
      duration: 0.5,
    },
  },
};

const WorkPage = () => {
  const seo = pages.work;
  const schema = buildGraph([
    webpageSchema(seo),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Projects", path: seo.path },
    ]),
  ]);
  const ref = useRef(null);

  const yinyang = useRef(null);

  useEffect(() => {
    const slider = ref.current;
    if (!slider) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const pageX = (e) =>
      e.touches && e.touches.length ? e.touches[0].pageX : e.pageX;

    const onDown = (e) => {
      isDown = true;
      slider.classList.add("active");
      startX = pageX(e) - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    };

    const onUp = () => {
      isDown = false;
      slider.classList.remove("active");
    };

    const onMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = pageX(e) - slider.offsetLeft;
      const walk = (x - startX) * 2;
      slider.scrollLeft = scrollLeft - walk;
      if (yinyang.current) {
        yinyang.current.style.transform =
          "rotate(" + slider.scrollLeft / 3 + "deg)";
      }
    };

    slider.addEventListener("mousedown", onDown);
    slider.addEventListener("mouseleave", onUp);
    slider.addEventListener("mouseup", onUp);
    slider.addEventListener("mousemove", onMove);
    slider.addEventListener("touchstart", onDown, { passive: true });
    slider.addEventListener("touchend", onUp);
    slider.addEventListener("touchcancel", onUp);
    slider.addEventListener("touchmove", onMove, { passive: false });

    return () => {
      slider.removeEventListener("mousedown", onDown);
      slider.removeEventListener("mouseleave", onUp);
      slider.removeEventListener("mouseup", onUp);
      slider.removeEventListener("mousemove", onMove);
      slider.removeEventListener("touchstart", onDown);
      slider.removeEventListener("touchend", onUp);
      slider.removeEventListener("touchcancel", onUp);
      slider.removeEventListener("touchmove", onMove);
    };
  }, []);

  return (
    <ThemeProvider theme={DarkTheme}>
      <Seo
        title={seo.title}
        description={seo.description}
        path={seo.path}
        schema={schema}
      />
      <Suspense fallback={<Loading />}>
        <Box
          key="work"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 1 } }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
        >
          <PageHeading>Projects by Hareesh Rajendran</PageHeading>
          <LogoComponent theme="dark" />
          <PowerButton />
          <SocialIcons theme="dark" />

          <Main
            className="items"
            id="grab-scroll"
            ref={ref}
            variants={container}
            initial="hidden"
            animate="show"
          >
            {Work.map((d) => (
              <Card key={d.id} data={d} />
            ))}
          </Main>

          <BigTitle text="WORK" top="10%" right="20%" />
          <BigTitle text="Swipe.." top="80%" right="0%" />

          <Rotate ref={yinyang}>
            <YinYang width={80} height={80} fill={DarkTheme.text} />
          </Rotate>
        </Box>
      </Suspense>
    </ThemeProvider>
  );
};

export default WorkPage;
